import React, { useState, useEffect } from 'react';
import { Phone, X } from 'lucide-react';
import { COMPANY_INFO } from '../config/assets';

export const CallNowButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Show button after 2 seconds when homepage loads
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Auto-expand after another 2 seconds
      setTimeout(() => setIsExpanded(true), 2000);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleCallClick = () => {
    window.location.href = `tel:${COMPANY_INFO.phone}`;
  };

  const handleClose = () => {
    setIsExpanded(false);
    setIsMinimized(true);
  };

  const handleExpand = () => {
    setIsExpanded(true);
    setIsMinimized(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-1/2 left-6 -translate-y-1/2 z-40">
      <div className="flex items-center gap-4">
        {/* Expanded Message Card */}
        {isExpanded && !isMinimized && (
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-amber-900 dark:to-orange-900 text-white rounded-lg shadow-2xl p-6 max-w-[320px] animate-in fade-in slide-in-from-left-2 duration-500 border-l-4 border-amber-500">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 rounded-full p-2">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white">
                    Call Our Experts
                  </h3>
                  <p className="text-amber-200 text-sm">
                    Free consultation & quotes
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="call-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-slate-200 text-sm mb-4 leading-relaxed">
              🏆 <span className="font-medium">Ready to transform your space?</span>
              <br />
              Speak directly with our concrete specialists for expert advice and instant quotes.
            </p>
            
            <button
              onClick={handleCallClick}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg py-3 px-6 font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              data-testid="call-now-button"
            >
              <Phone className="w-5 h-5 animate-pulse" />
              {COMPANY_INFO.phone}
            </button>
            
            <div className="mt-3 text-center">
              <p className="text-amber-200 text-xs">
                Available Mon-Fri 7AM-6PM AEST
              </p>
            </div>
          </div>
        )}

        {/* Main Call Button - Show when not expanded OR when minimized */}
        {(!isExpanded || isMinimized) && (
          <div className="relative">
            <button
              onClick={isMinimized ? handleExpand : handleCallClick}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-full p-4 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 relative group"
              data-testid="call-main-button"
              aria-label={isMinimized ? "Show call options" : "Call Now"}
            >
              <Phone className="w-6 h-6 group-hover:animate-bounce" />
              
              {/* Attention indicator - only show when not minimized */}
              {!isMinimized && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 border-2 border-white rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              )}
              
              {/* Tooltip for minimized state */}
              {isMinimized && (
                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 bg-slate-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  Click to call or see details
                  <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-slate-800"></div>
                </div>
              )}
            </button>
            
            {/* Pulse animation - only when first appearing, not when minimized */}
            {!isMinimized && (
              <>
                <div className="absolute inset-0 bg-amber-500 rounded-full animate-ping opacity-30"></div>
                <div className="absolute inset-0 bg-orange-500 rounded-full animate-ping opacity-20 animation-delay-500"></div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};